/*
 * selectors.js
 *
 * Selectors provide different views of state.
 */
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { createSelector } from "reselect";

import auditLogs from "../policies/selectors/audit-logs";

const logsTableSelectors = {};

export const logsTableActionMap = new Map([
  ["CREATE", "Added"],
  ["DELETE", "Removed"],
  ["UPDATE", "Updated"],
  ["REGISTER", "Registered"]
]);

/**
 * Returns list of logs formatted to populate log tables.
 * @returns {Object[]} logs
 */
logsTableSelectors.get = createSelector(
  [auditLogs.getHydrated],
  logs => {
    const formattedLogs = logs.map(log => {
      const { $uri, actor, changeset, target, when, action, message } = log;

      const [day, time] = new Date(when.$date).toLocaleString().split(", ");

      return {
        $uri,
        date: {
          row1: day,
          row2: time,
          utc: when.$date
        },
        event: logsTableActionMap.get(action),
        activity: target ? `${target.type}:${target.entity_id}` : "-",
        author: actor && {
          $uri: actor.$uri,
          label: `${actor.type}:${actor.entity_id}`
        },
        description: message,
        message: changeset && changeset.message ? changeset.message : null
      };
    });

    return formattedLogs;
  }
);

/**
 * Returns list of unique authors in logs list.
 * @returns {Object[]} options
 */
logsTableSelectors.getUniqueAuthors = createSelector(
  [logsTableSelectors.get],
  logs => {
    const logsWithAuthors = logs.filter(log => log.author);
    const uniqueLogs = uniqBy(logsWithAuthors, "author.label");

    const authors = uniqueLogs.map(log => ({
      key: log.author.$uri,
      label: log.author.label
    }));

    return orderBy(authors, [author => author.label], ["asc"]);
  }
);

export default logsTableSelectors;
